import { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ChevronRight } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { PageHeroCard } from "@/components/site/PageHeroCard";
import { Button } from "@/components/ui/button";
import { useLang } from "@/i18n/LangContext";
import {
  getGuideArticle,
  guidesInCategory,
  guideHref,
  localizeGuideLink,
  type GuideSection,
  type GuideArticle,
} from "@/data/guideArticles";
import { PageMeta } from "@/components/site/PageMeta";

// ─── section renderer ───────────────────────────────────────────────────────
const Section = ({
  section,
  lang,
}: {
  section: GuideSection;
  lang: "sv" | "en";
}) => {
  const title = section.title[lang];
  const paragraphs = section.paragraphs[lang];
  const items = section.items?.[lang];
  const table = section.table;
  const link = section.link;

  return (
    <div id={section.id} className="scroll-mt-24">
      <h2 className="font-display text-xl font-bold tracking-tight text-foreground sm:text-2xl">
        {title}
      </h2>
      {paragraphs.map((p, i) => (
        <p key={i} className="mt-3 text-base leading-relaxed text-muted-foreground">
          {p}
        </p>
      ))}
      {items && items.length > 0 && (
        <ul className="mt-4 space-y-2">
          {items.map((item, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm text-muted-foreground">
              <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}
      {table && (
        <div className="mt-4 overflow-x-auto rounded-xl border border-border/60">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-border/60 bg-muted/40">
                {table.columns[lang].map((col, i) => (
                  <th
                    key={i}
                    className="px-4 py-3 text-left font-semibold text-foreground"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows[lang].map((row, ri) => (
                <tr
                  key={ri}
                  className={ri % 2 === 0 ? "bg-white" : "bg-muted/20"}
                >
                  {row.map((cell, ci) => (
                    <td
                      key={ci}
                      className="px-4 py-3 text-muted-foreground"
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {link && (
        <div className="mt-4">
          <Link
            to={localizeGuideLink(link.href, lang)}
            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
          >
            {link.label[lang]}
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      )}
    </div>
  );
};

// ─── main page ───────────────────────────────────────────────────────────────
const GuideDetail = () => {
  const { lang } = useLang();
  const { category, slug } = useParams<{ category: string; slug: string }>();
  const [openSection, setOpenSection] = useState<string | null>(null);

  const article: GuideArticle | null =
    category && slug ? getGuideArticle(category, slug) : null;

  const related = category
    ? guidesInCategory(category, slug).slice(0, 3)
    : [];


  if (!article) {
    return (
      <Navigate
        to={lang === "sv" ? "/guider" : "/en/guides"}
        replace
      />
    );
  }

  const guidesHref = lang === "sv" ? "/guider" : "/en/guides";
  const guidesLabel = lang === "sv" ? "Guider" : "Guides";
  const homeLabel = lang === "sv" ? "Hem" : "Home";
  const homeHref = lang === "sv" ? "/" : "/en";
  const relatedLabel = lang === "sv" ? "Fler guider i samma ämne" : "More guides on this topic";
  const readLabel = lang === "sv" ? "Läs guiden" : "Read the guide";
  const sourceLabel = lang === "sv" ? "Källhänvisning" : "Source";
  const bookLabel = lang === "sv" ? "Boka tid" : "Book an appointment";
  const bookingTo = article.bookingHref
    ? localizeGuideLink(article.bookingHref, lang)
    : lang === "sv"
      ? "/boka-tid"
      : "/en/book-appointment";

  void openSection; // used for future accordion toggling

  return (
    <div className="min-h-screen bg-background">
      <PageMeta title={`${article.title[lang]} | Intygsläkaren`} description={article.description[lang]} />
      <Header />
      <main>
        {/* Hero */}
        <PageHeroCard compact>
          <div className="animate-fade-up max-w-2xl">
            <span className="inline-flex h-7 items-center justify-center rounded-full bg-white/20 px-3.5 text-xs font-semibold uppercase tracking-wider text-foreground/70 backdrop-blur-sm">
              {article.label[lang]}
            </span>
            <h1 className="mt-4 text-balance font-display text-3xl font-extrabold leading-[1.08] tracking-tight text-foreground sm:text-4xl lg:text-5xl">
              {article.title[lang]}
            </h1>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              {article.description[lang]}
            </p>
            {article.bookingHref && (
              <div className="mt-6">
                <Button asChild variant="hero" size="lg">
                  <Link to={bookingTo}>
                    {bookLabel}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            )}
          </div>
        </PageHeroCard>

        {/* Article body */}
        <section className="py-12 lg:py-16">
          <div className="container-wide">
            <article className="mx-auto max-w-3xl space-y-10">
              {article.sections.map((section) => (
                <Section
                  key={section.id}
                  section={section}
                  lang={lang}
                />
              ))}

              {/* Source note */}
              {article.source && (
                <p className="border-t border-border/40 pt-6 text-xs text-muted-foreground/70">
                  <span className="font-semibold">{sourceLabel}:</span>{" "}
                  {article.source[lang]}
                </p>
              )}
            </article>
          </div>
        </section>

        {/* Related guides */}
        {related.length > 0 && (
          <section className="pb-16 lg:pb-20">
            <div className="container-wide">
              <h2 className="font-display text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
                {relatedLabel}
              </h2>
              <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((r) => (
                  <Link
                    key={`${r.category}/${r.slug}`}
                    to={guideHref(r.category, r.slug, lang)}
                    className="group flex flex-col rounded-2xl border border-border/60 bg-card p-6 shadow-card transition-smooth hover:-translate-y-1 hover:border-primary/30 hover:shadow-elevated sm:rounded-3xl"
                  >
                    <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                      {r.label[lang]}
                    </span>
                    <h3 className="mt-2 font-display text-lg font-semibold leading-snug text-foreground">
                      {r.title[lang]}
                    </h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground line-clamp-3">
                      {r.description[lang]}
                    </p>
                    <span className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-primary">
                      {readLabel}
                      <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA banner */}
        <section className="px-2 py-16 sm:px-3 lg:px-4 lg:py-20">
          <div className="relative mx-auto w-full max-w-[96rem] overflow-hidden rounded-2xl bg-primary px-8 py-14 text-primary-foreground shadow-elevated sm:rounded-3xl lg:px-16 lg:py-20">
            <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
            <div className="absolute -bottom-24 -left-10 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
            <div className="relative max-w-2xl">
              <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
                {lang === "sv"
                  ? "Redo att gå vidare?"
                  : "Ready to proceed?"}
              </h2>
              <p className="mt-3 text-base text-primary-foreground/85">
                {lang === "sv"
                  ? "Boka en tid direkt – video eller fysiskt besök i Göteborg."
                  : "Book an appointment – video or in-person in Gothenburg."}
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Button asChild variant="onPrimary" size="xl">
                  <Link to={bookingTo}>
                    {bookLabel}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="ghostOnPrimary" size="xl">
                  <Link to={guidesHref}>
                    {guidesLabel}
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <Breadcrumbs
          items={[
            { label: homeLabel, to: homeHref },
            { label: guidesLabel, to: guidesHref },
            { label: article.title[lang] },
          ]}
        />
      </main>
      <Footer />
    </div>
  );
};

export default GuideDetail;
